import { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Restore, DeleteOutline, FolderZip } from "@mui/icons-material";
import JSZip from "jszip";

import DropFiles from "e.sic-platform.webclient/src/components/upload-drop-files/DropFiles";
import { FileExtended } from "e.sic-platform.webclient/src/services/upload/FileUploadTracker";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { snackbarNotificationsService } from "e.sic-platform.webclient/src/services/notifications/SnackbarNotificationsService";
import { restoreManager } from "../../services/restore/RestoreManager";

export interface RestoreDataDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal "form" for kicking off a restore. Holds the chosen backup file in
 * LOCAL state until the user clicks Start Restoration — only then is it added
 * to the shared `uploadFilesService` and the upload+restore is triggered.
 * This prevents the file from leaking into the underlying app's upload table.
 */
export default observer(function RestoreDataDialog({ open, onClose }: RestoreDataDialogProps) {
  const t = (key: string) => translationService.translate(key);

  const [stagedFiles, setStagedFiles] = useState<Array<FileExtended>>([]);
  const [validating, setValidating] = useState(false);

  // Clear staged file every time the modal is opened, so a previous run's
  // file doesn't reappear.
  useEffect(() => {
    if (open) setStagedFiles([]);
  }, [open]);

  const validateAndStageFiles = useCallback(async (files: Array<FileExtended>) => {
    if (files.length === 0) return;
    setValidating(true);
    try {
      const file = files[0];
      const arrayBuffer = await file.file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);
      const names = Object.keys(zip.files);
      const hasMp4 = names.some((n) => n.toLowerCase().endsWith(".mp4"));
      const hasMetadata = names.some((n) => n === "metadata.json.esic");
      if (!hasMp4) {
        snackbarNotificationsService.showError("restoreEsicData.error.noVideo");
        return;
      }
      if (!hasMetadata) {
        snackbarNotificationsService.showError("restoreEsicData.error.noMetadata");
        return;
      }
      setStagedFiles([file]);
    } catch {
      snackbarNotificationsService.showError("restoreEsicData.error.noMetadata");
    } finally {
      setValidating(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    setStagedFiles([]);
    onClose();
  }, [onClose]);

  const handleStart = useCallback(() => {
    if (stagedFiles.length === 0) return;
    // Hand the file off to the manager. It enqueues the op and starts it
    // automatically when nothing else is running (sequential queue).
    restoreManager.submit(stagedFiles[0]);
    setStagedFiles([]);
    onClose();
  }, [stagedFiles, onClose]);

  const hasFile = stagedFiles.length > 0;
  const fileName = hasFile ? stagedFiles[0].file.name : "";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("restoreEsicData.button.run")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t("restoreEsicData.description")}
          </Typography>
          <DropFiles
            singleFile
            onDroppedFiles={validateAndStageFiles}
            fileTypesAccepted="application/zip,.zip,application/x-zip-compressed"
          />
          {hasFile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 2,
                py: 1,
              }}
            >
              <FolderZip color="primary" />
              <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                {fileName}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setStagedFiles([])}
                aria-label="remove"
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("restoreEsicData.cancel")}</Button>
        <Button
          variant="contained"
          startIcon={<Restore />}
          disabled={!hasFile || validating}
          onClick={handleStart}
        >
          {t("restoreEsicData.start")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
